import React from 'react';
import { Box, Card, CardActionArea, CardContent, CardMedia, Grid, Typography } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { IconName } from '@fortawesome/fontawesome-svg-core';
import BodyBanner from '../components/home/body-banner';
import QuickAccessCard from '../components/home/quick-access-card';
import { RichTextContent } from '../custom-page/rich-text-content';
import SectionNews from './sections/section-news';
import SectionPublicationsTabs from './sections/section-publications-tabs';
import { useMenuLinkNavigation } from '../../hooks/useMenuLinkNavigation';
import { youtubeEmbedUrl } from '../../utils/youtube';
import { IBannerReponse } from '../../model/base/response/banner-response';
import {
  HomeSectionType,
  IPortalHomeSectionItemResponse,
  IPortalHomeSectionResponse,
} from '../../model/base/response/portal-home-response';

/** Quantidade padrão de notícias quando a seção não define (mesma da home padrão). */
export const DEFAULT_NEWS_QUANTITY = 4;

// Ícones do Acesso Rápido: chaves gravadas pelo APM (ACESSO_RAPIDO_ICONS) → ícones FontAwesome do portal
const ACCESS_ICONS: Record<string, IconName> = {
  description: 'file-lines',
  gavel: 'gavel',
  account_balance: 'landmark',
  payments: 'money-bill-wave',
  receipt_long: 'receipt',
  shopping_cart: 'cart-shopping',
  work: 'briefcase',
  groups: 'users',
  support_agent: 'headset',
  campaign: 'bullhorn',
  visibility: 'eye',
  event: 'calendar-days',
  newspaper: 'newspaper',
  menu_book: 'book-open',
  health_and_safety: 'shield-heart',
  school: 'graduation-cap',
  phone: 'phone',
  place: 'location-dot',
  link: 'link',
};

function SectionHeading({ title, subtitle }: { title?: string; subtitle?: string }) {
  if (!title && !subtitle) return null;

  return (
    <>
      {title && (
        <Typography
          variant="h2"
          component="h2"
          sx={{ mb: subtitle ? 2 : 4, fontWeight: 700, color: 'primary.main', textAlign: 'left' }}
        >
          {title}
        </Typography>
      )}
      {subtitle && (
        <Typography variant="body1" sx={{ mb: 4, textAlign: 'left', color: 'text.secondary' }}>
          {subtitle}
        </Typography>
      )}
    </>
  );
}

// Banner de corpo: posicionado entre as seções, com layout próprio (o carrossel do topo é renderizado pela HomeComponent)
function BannersSection({ section }: { section: IPortalHomeSectionResponse }) {
  const sectionBanners: IBannerReponse[] = (section.items || [])
    .filter((item) => !!item.imageUrl)
    .map((item) => ({
      id: item.id,
      title: item.title,
      urlImage: item.imageUrl,
      nomImgBanner: item.image,
      url: item.url,
      openInNewTab: item.openInNewTab,
    }));

  if (sectionBanners.length === 0) return null;

  return (
    <Box component="section" sx={{ my: { xs: 3, md: 5 } }}>
      <BodyBanner banners={sectionBanners} />
    </Box>
  );
}

function FreeTextSection({ section }: { section: IPortalHomeSectionResponse }) {
  if (!section.content) return null;

  return (
    <Box component="section" sx={{ mb: 6 }}>
      <SectionHeading title={section.title} subtitle={section.subtitle} />
      <RichTextContent html={section.content} />
    </Box>
  );
}

/** Link externo cadastrado sem protocolo vira https; o card só trata como externo o que começa com http. */
const accessHref = (url: string | undefined) => {
  if (!url) return '#';
  return /^[\w-]+(\.[\w-]+)+(\/|$)/i.test(url) ? `https://${url}` : url;
};

function QuickAccessSection({ section }: { section: IPortalHomeSectionResponse }) {
  if (!section.items?.length) return null;

  return (
    <Box component="section" sx={{ mb: 6 }}>
      <SectionHeading title={section.title || 'Acesso Rápido'} subtitle={section.subtitle} />
      <Grid container spacing={3}>
        {(section.items || []).map((item) => (
          <Grid item xs={6} sm={4} md={2.4} key={item.id}>
            <QuickAccessCard
              title={item.title || ''}
              icon={(item.icon && ACCESS_ICONS[item.icon]) || 'link'}
              href={accessHref(item.url)}
              openInNewTab={item.openInNewTab}
            />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}

function HighlightCard({ item }: { item: IPortalHomeSectionItemResponse }) {
  const openLink = useMenuLinkNavigation();

  const body = (
    <>
      {item.imageUrl && (
        <CardMedia component="img" image={item.imageUrl} alt={item.title || ''} sx={{ height: 200, objectFit: 'cover' }} />
      )}
      <CardContent>
        <Typography variant="h6" component="h3" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
          {item.title}
        </Typography>
        {item.description && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, whiteSpace: 'pre-line' }}>
            {item.description}
          </Typography>
        )}
        {item.url && (
          <Typography
            variant="caption"
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1.5, color: 'primary.main', fontWeight: 600, textTransform: 'uppercase' }}
          >
            Saiba mais <ArrowForwardIcon sx={{ fontSize: '1rem' }} />
          </Typography>
        )}
      </CardContent>
    </>
  );

  return (
    <Card elevation={2} sx={{ height: '100%', borderRadius: 2 }}>
      {item.url ? (
        <CardActionArea
          sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'stretch', justifyContent: 'flex-start' }}
          onClick={() => openLink(item.url, item.openInNewTab)}
        >
          {body}
        </CardActionArea>
      ) : body}
    </Card>
  );
}

function HighlightCardsSection({ section }: { section: IPortalHomeSectionResponse }) {
  const items = section.items || [];
  if (!items.length) return null;

  const md = items.length >= 3 ? 4 : 12 / items.length;

  return (
    <Box component="section" sx={{ mb: 6 }}>
      <SectionHeading title={section.title || 'Destaques'} subtitle={section.subtitle} />
      <Grid container spacing={3}>
        {items.map((item) => (
          <Grid item xs={12} sm={6} md={md} key={item.id}>
            <HighlightCard item={item} />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}

function VideosSection({ section }: { section: IPortalHomeSectionResponse }) {
  const videos = (section.items || [])
    .map((item) => ({ item, embedUrl: youtubeEmbedUrl(item.url) }))
    .filter((video): video is { item: IPortalHomeSectionItemResponse; embedUrl: string } => !!video.embedUrl);

  if (!videos.length) return null;

  const md = videos.length >= 3 ? 4 : 12 / videos.length;

  return (
    <Box component="section" sx={{ mb: 6 }}>
      <SectionHeading title={section.title || 'Vídeos'} subtitle={section.subtitle} />
      <Grid container spacing={3}>
        {videos.map(({ item, embedUrl }) => (
          <Grid item xs={12} sm={6} md={md} key={item.id}>
            <Card elevation={2} sx={{ borderRadius: 2 }}>
              <Box sx={{ position: 'relative', pt: '56.25%' }}>
                <Box
                  component="iframe"
                  src={embedUrl}
                  title={item.title || 'Vídeo'}
                  loading="lazy"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }}
                />
              </Box>
              {item.title && (
                <Typography fontWeight={600} sx={{ p: 2 }}>
                  {item.title}
                </Typography>
              )}
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}

function HomeSection({ section }: { section: IPortalHomeSectionResponse }) {
  switch (section.type) {
    case HomeSectionType.Banners:
      return <BannersSection section={section} />;
    case HomeSectionType.DiarioOficial:
      return <SectionPublicationsTabs title={section.title} subtitle={section.subtitle} />;
    case HomeSectionType.Noticias:
      return <SectionNews title={section.title} subtitle={section.subtitle} quantity={section.itemsQuantity || DEFAULT_NEWS_QUANTITY} />;
    case HomeSectionType.TextoLivre:
      return <FreeTextSection section={section} />;
    case HomeSectionType.AcessoRapido:
      return <QuickAccessSection section={section} />;
    case HomeSectionType.CardDestaque:
      return <HighlightCardsSection section={section} />;
    case HomeSectionType.Videos:
      return <VideosSection section={section} />;
    default:
      return null;
  }
}

/** Home montada pela customização do APM, na ordem e com as seções visíveis que a API devolve. */
export function CustomHome({ sections }: { sections: IPortalHomeSectionResponse[] }) {
  return (
    <Box sx={{ py: 0 }}>
      {sections.map((section) => (
        <HomeSection key={section.id} section={section} />
      ))}
    </Box>
  );
}
